<!-- app-subdomain-creators/src/Components/NewsfeedSettingsModal.vue -->

<template>
  <TransitionRoot
    as="template"
    :show="isOpen"
  >
    <Dialog
      as="div"
      class="relative z-50"
      @close="emitClose"
    >
      <!-- Overlay -->
      <TransitionChild
        as="template"
        enter="ease-out duration-150"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-white/75 backdrop-blur-[1px]" />
      </TransitionChild>

      <!-- Panel -->
      <div class="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6">
        <TransitionChild
          as="template"
          enter="ease-out duration-150"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-100"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <DialogPanel
            class="w-full max-w-2xl overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl"
          >
            <!-- Header -->
            <div class="relative flex items-center justify-center border-b border-zinc-200 px-6 py-4">
              <DialogTitle class="text-xl font-semibold text-zinc-900">
                Newsfeed Settings
              </DialogTitle>
              <button
                type="button"
                class="absolute right-4 rounded-full p-1.5 text-zinc-500 bg-zinc-200 hover:bg-zinc-300 hover:text-zinc-700"
                @click="emitClose"
              >
                <span class="sr-only">Close</span>
                <XMarkIcon class="w-6 h-6" />
              </button>
            </div>

            <!-- Body -->
            <div class="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto app-scroll">
              <!-- Posting as profile -->
              <section class="rounded-xl border border-zinc-200 p-4">
                <h3 class="text-base font-semibold text-zinc-900">
                  Posting as profile
                </h3>
                <p class="mt-1 text-xs text-zinc-500">
                  Choose the name and avatar members will see when you post in this group.
                </p>

                <div class="mt-4 flex items-center gap-4">
                  <!-- Avatar preview + quick action -->
                  <div>
                    <!-- Clickable avatar circle to open file picker -->
                    <div
                      class="h-16 w-16 rounded-full overflow-hidden bg-zinc-200 flex items-center justify-center cursor-pointer"
                      @click="triggerAvatarFilePicker"
                    >
                      <img
                        v-if="localAvatarUrl"
                        :src="localAvatarUrl"
                        alt="Posting avatar"
                        class="h-full w-full object-cover"
                      >
                      <span
                        v-else
                        class="text-[10px] text-zinc-500 text-center px-1"
                      >
                        Click to upload
                      </span>
                    </div>

                    <!-- Hidden file input for avatar upload -->
                    <input
                      ref="avatarFileInputRef"
                      type="file"
                      accept="image/*"
                      class="hidden"
                      @change="handleAvatarFileSelected"
                    >

                    <button
                      v-if="defaultProfileAvatarUrl"
                      type="button"
                      class="mt-2 block text-xs text-blue-600 hover:text-blue-700"
                      @click="useProfileAvatar"
                    >
                      Use profile avatar
                    </button>
                  </div>

                  <!-- Inputs -->
                  <div class="flex-1 space-y-3">
                    <div>
                      <label class="block text-xs font-medium text-zinc-700">
                        Display name
                      </label>
                      <input
                        v-model="localName"
                        type="text"
                        class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        :placeholder="defaultProfileName || 'Your creator name'"
                      >
                    </div>

                    <div>
                      <label class="block text-xs font-medium text-zinc-700">
                        Avatar image URL
                      </label>
                      <input
                        v-model="localAvatarUrl"
                        type="text"
                        class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Paste an image URL or click the avatar to upload"
                      >
                    </div>
                  </div>
                </div>
              </section>

              <section class="rounded-xl border border-zinc-200 p-4">
                <h3 class="text-base font-semibold text-zinc-900">
                  Audience display
                </h3>
                <p class="mt-1 text-xs text-zinc-500">
                  Control what appears in the group header.
                </p>

                <div class="mt-4 flex items-center justify-between gap-4">
                  <div>
                    <p class="text-sm font-medium text-zinc-800">
                      Hide member count
                    </p>
                    <p class="text-xs text-zinc-500">
                      Hide the members total shown beside the Private group label.
                    </p>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    :aria-checked="localHideMemberCount ? 'true' : 'false'"
                    class="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
                    :class="localHideMemberCount ? 'bg-[#2D8CFF]' : 'bg-zinc-300'"
                    @click="localHideMemberCount = !localHideMemberCount"
                  >
                    <span
                      class="inline-block h-5 w-5 transform rounded-full bg-white transition"
                      :class="localHideMemberCount ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </section>

              <section class="rounded-xl border border-zinc-200 p-4">
                <h3 class="text-base font-semibold text-zinc-900">
                  Post moderation
                </h3>
                <p class="mt-1 text-xs text-zinc-500">
                  Control whether member posts require creator approval.
                </p>

                <div class="mt-4 flex items-center justify-between gap-4">
                  <div>
                    <p class="text-sm font-medium text-zinc-800">
                      Require post approval
                    </p>
                    <p class="text-xs text-zinc-500">
                      Member-created posts will be saved as pending approval.
                    </p>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    :aria-checked="localRequirePostApproval ? 'true' : 'false'"
                    class="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
                    :class="localRequirePostApproval ? 'bg-[#2D8CFF]' : 'bg-zinc-300'"
                    @click="localRequirePostApproval = !localRequirePostApproval"
                  >
                    <span
                      class="inline-block h-5 w-5 transform rounded-full bg-white transition"
                      :class="localRequirePostApproval ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </section>

              <section class="rounded-xl border border-zinc-200 p-4">
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <h3 class="text-base font-semibold text-zinc-900">
                      Import Bulk Posts
                    </h3>
                    <p class="mt-1 text-xs text-zinc-500">
                      Bring in older posts with dry-run preview, live progress, and rollback.
                    </p>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-md border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                      @click="openBackPostComposer"
                    >
                      <PlusIcon class="h-4 w-4" />
                      Create Back Post
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-md bg-zinc-900 px-3 py-2 text-xs font-semibold text-white hover:bg-zinc-800"
                      @click="openImportWizard"
                    >
                      <ArrowDownTrayIcon class="h-4 w-4" />
                      Import Bulk Posts
                    </button>
                  </div>
                </div>
              </section>

              <p
                v-if="isLoading"
                class="text-xs text-zinc-400"
              >
                Loading current settingsâ€¦
              </p>
            </div>

            <!-- Footer -->
            <div
              class="flex justify-end gap-3 border-t bg-zinc-50 px-6 py-4"
            >
              <button
                type="button"
                class="px-3 py-1.5 text-sm text-zinc-600 hover:text-zinc-900"
                @click="emitClose"
              >
                Cancel
              </button>
              <button
                type="button"
                class="inline-flex items-center rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                :disabled="isSaving"
                @click="handleSave"
              >
                <span v-if="!isSaving">Save settings</span>
                <span v-else>Savingâ€¦</span>
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <TransitionRoot
    as="template"
    :show="importWizardOpen"
  >
    <Dialog
      as="div"
      class="relative z-[80]"
      @close="handleImportWizardRequestClose"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-150"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-zinc-950/30 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 z-[80] flex items-center justify-center px-4 sm:px-6">
        <TransitionChild
          as="template"
          enter="ease-out duration-150"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-100"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <DialogPanel class="w-full max-w-3xl overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
            <div class="relative flex items-center justify-between border-b border-zinc-200 px-6 py-4">
              <div>
                <DialogTitle class="text-xl font-semibold text-zinc-900">
                  Import Posts
                </DialogTitle>
                <p class="mt-1 text-xs text-zinc-500">
                  Step {{ importWizardStep }} of 3: {{ importWizardStepLabel }}
                </p>
              </div>
              <button
                type="button"
                class="rounded-full p-1.5 text-zinc-500 bg-zinc-200 hover:bg-zinc-300 hover:text-zinc-700"
                @click="closeImportWizard"
              >
                <span class="sr-only">Close</span>
                <XMarkIcon class="w-6 h-6" />
              </button>
            </div>

            <div class="px-6 py-5 space-y-5 max-h-[72vh] overflow-y-auto app-scroll">
              <section
                v-if="importWizardStep === 1"
                class="space-y-4"
              >
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label class="block text-xs font-medium text-zinc-700">Source</label>
                    <PlatformaSelect
                      v-model="importSourceType"
                      :options="importSourceTypeSelectOptions"
                      placeholder="Select source"
                    />
                  </div>
                  <div v-if="importSourceType === 'platforma_newsfeed_instance'">
                    <label class="block text-xs font-medium text-zinc-700">Source instance</label>
                    <PlatformaSelect
                      v-model="importSourceFeatureInstanceId"
                      :options="importSourceInstanceSelectOptions"
                      :disabled="isLoadingImportSources || !importSourceOptions.length"
                      :placeholder="isLoadingImportSources ? 'Loading instances...' : 'Select source instance'"
                    />
                  </div>
                  <div v-else>
                    <label class="block text-xs font-medium text-zinc-700">Upload CSV file</label>
                    <input
                      type="file"
                      accept=".csv,.txt,.tsv,text/csv,text/tab-separated-values"
                      class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      @change="handleImportCsvFileSelected"
                    >
                    <p
                      v-if="importCsvFileName"
                      class="mt-1 text-xs text-zinc-500"
                    >
                      Loaded: {{ importCsvFileName }}
                    </p>
                  </div>
                </div>

                <div v-if="importSourceType === 'legacy_newsfeed_csv'">
                  <label class="block text-xs font-medium text-zinc-700">Legacy CSV payload</label>
                  <textarea
                    v-model="importCsvPayload"
                    rows="8"
                    class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Paste your legacy Newsfeed CSV/TSV export here..."
                  />
                </div>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label class="block text-xs font-medium text-zinc-700">Timeframe</label>
                    <PlatformaSelect
                      v-model="importTimeframe"
                      :options="importTimeframeSelectOptions"
                      placeholder="Select timeframe"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-zinc-700">Dedupe mode</label>
                    <PlatformaSelect
                      v-model="importDedupeMode"
                      :options="importDedupeModeSelectOptions"
                      placeholder="Select dedupe mode"
                    />
                  </div>
                </div>

                <div
                  v-if="importTimeframe === 'custom'"
                  class="grid grid-cols-1 gap-4 md:grid-cols-2"
                >
                  <div>
                    <label class="block text-xs font-medium text-zinc-700">Start date</label>
                    <input
                      v-model="importCustomStartDate"
                      type="date"
                      class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-zinc-700">End date</label>
                    <input
                      v-model="importCustomEndDate"
                      type="date"
                      class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                  </div>
                </div>

                <div class="rounded-xl border border-zinc-200 p-4 flex items-center justify-between gap-4">
                  <div>
                    <p class="text-sm font-medium text-zinc-800">
                      Include media
                    </p>
                    <p class="text-xs text-zinc-500">
                      Keep image/video references from imported posts.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="importIncludeMedia ? 'true' : 'false'"
                    class="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
                    :class="importIncludeMedia ? 'bg-[#2D8CFF]' : 'bg-zinc-300'"
                    @click="importIncludeMedia = !importIncludeMedia"
                  >
                    <span
                      class="inline-block h-5 w-5 transform rounded-full bg-white transition"
                      :class="importIncludeMedia ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>

                <p
                  v-if="importError"
                  class="text-sm text-red-600"
                >
                  {{ importError }}
                </p>
              </section>

              <section
                v-if="importWizardStep === 2"
                class="space-y-4"
              >
                <p
                  v-if="isPreviewLoading"
                  class="text-sm text-zinc-500"
                >
                  Building dry-run preview...
                </p>

                <template v-else-if="importPreview">
                  <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div class="rounded-lg border border-zinc-200 p-3">
                      <p class="text-[11px] uppercase tracking-wide text-zinc-500">
                        Source
                      </p>
                      <p class="mt-1 text-lg font-semibold text-zinc-900">
                        {{ importPreview.sourceCount || 0 }}
                      </p>
                    </div>
                    <div class="rounded-lg border border-zinc-200 p-3">
                      <p class="text-[11px] uppercase tracking-wide text-zinc-500">
                        Would import
                      </p>
                      <p class="mt-1 text-lg font-semibold text-emerald-600">
                        {{ importPreview.wouldImport || 0 }}
                      </p>
                    </div>
                    <div class="rounded-lg border border-zinc-200 p-3">
                      <p class="text-[11px] uppercase tracking-wide text-zinc-500">
                        Would skip
                      </p>
                      <p class="mt-1 text-lg font-semibold text-amber-600">
                        {{ importPreview.wouldSkip || 0 }}
                      </p>
                    </div>
                    <div class="rounded-lg border border-zinc-200 p-3">
                      <p class="text-[11px] uppercase tracking-wide text-zinc-500">
                        Date range
                      </p>
                      <p class="mt-1 text-xs font-medium text-zinc-700">
                        {{ importPreview.dateRangeLabel || 'All time' }}
                      </p>
                    </div>
                  </div>

                  <div class="rounded-xl border border-zinc-200 overflow-hidden">
                    <div class="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                      <h4 class="text-sm font-semibold text-zinc-900">
                        Sample preview
                      </h4>
                    </div>
                    <div class="max-h-[280px] overflow-y-auto app-scroll">
                      <table class="min-w-full text-sm">
                        <thead class="bg-white sticky top-0">
                          <tr class="text-left text-xs uppercase tracking-wide text-zinc-500">
                            <th class="px-4 py-2">
                              Published
                            </th>
                            <th class="px-4 py-2">
                              Action
                            </th>
                            <th class="px-4 py-2">
                              Reason
                            </th>
                            <th class="px-4 py-2">
                              Preview
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="row in importPreview.sample || []"
                            :key="row.sourcePostId"
                            class="border-t border-zinc-100"
                          >
                            <td class="px-4 py-2 text-zinc-600">
                              {{ formatImportDate(row.publishedAt) }}
                            </td>
                            <td class="px-4 py-2">
                              <span
                                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                                :class="row.action === 'imported'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-amber-100 text-amber-700'"
                              >
                                {{ row.action === 'imported' ? 'Import' : 'Skip' }}
                              </span>
                            </td>
                            <td class="px-4 py-2 text-zinc-500">
                              {{ row.reason || '-' }}
                            </td>
                            <td class="px-4 py-2 text-zinc-700">
                              {{ row.previewText || '-' }}
                            </td>
                          </tr>
                          <tr v-if="!(importPreview.sample || []).length">
                            <td
                              colspan="4"
                              class="px-4 py-8 text-center text-zinc-400"
                            >
                              No posts found for this timeframe.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </template>
              </section>

              <section
                v-if="importWizardStep === 3"
                class="space-y-4"
              >
                <div class="rounded-xl border border-zinc-200 p-4">
                  <div class="flex items-center justify-between gap-3">
                    <div>
                      <h4 class="text-sm font-semibold text-zinc-900">
                        Import progress
                      </h4>
                      <p class="text-xs text-zinc-500">
                        Job {{ importJob?.jobPublicId || '-' }} â€¢ {{ importJob?.status || 'starting' }}
                      </p>
                    </div>
                    <p class="text-lg font-semibold text-zinc-900">
                      {{ importProgressPercent }}%
                    </p>
                  </div>
                  <div class="mt-3 h-2 w-full rounded-full bg-zinc-200">
                    <div
                      class="h-2 rounded-full bg-blue-600 transition-all"
                      :style="{ width: `${importProgressPercent}%` }"
                    />
                  </div>
                  <div class="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 text-xs text-zinc-600">
                    <div>Processed: {{ importJob?.progress?.processedRows || 0 }}</div>
                    <div>Imported: {{ importJob?.progress?.importedRows || 0 }}</div>
                    <div>Skipped: {{ importJob?.progress?.skippedRows || 0 }}</div>
                    <div>Failed: {{ importJob?.progress?.failedRows || 0 }}</div>
                  </div>
                </div>

                <div
                  v-if="importJob?.errorMessage"
                  class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {{ importJob.errorMessage }}
                </div>

                <div class="rounded-xl border border-zinc-200 overflow-hidden">
                  <div class="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
                    <h4 class="text-sm font-semibold text-zinc-900">
                      Import report
                    </h4>
                    <button
                      v-if="canRollbackImport"
                      type="button"
                      class="rounded-md border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
                      :disabled="isRollbackLoading"
                      @click="handleRollbackImport"
                    >
                      {{ isRollbackLoading ? 'Rolling back...' : 'Rollback Import' }}
                    </button>
                  </div>
                  <div class="max-h-[280px] overflow-y-auto app-scroll">
                    <table class="min-w-full text-sm">
                      <thead class="bg-white sticky top-0">
                        <tr class="text-left text-xs uppercase tracking-wide text-zinc-500">
                          <th class="px-4 py-2">
                            Source post
                          </th>
                          <th class="px-4 py-2">
                            Result
                          </th>
                          <th class="px-4 py-2">
                            Reason
                          </th>
                          <th class="px-4 py-2">
                            Imported post
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in importJobReportRows"
                          :key="`${row.sourcePostId}-${row.createdAt}`"
                          class="border-t border-zinc-100"
                        >
                          <td class="px-4 py-2 text-zinc-700">
                            #{{ row.sourcePostId }}
                          </td>
                          <td class="px-4 py-2 text-zinc-600">
                            {{ row.resultStatus }}
                          </td>
                          <td class="px-4 py-2 text-zinc-500">
                            {{ row.reason || '-' }}
                          </td>
                          <td class="px-4 py-2 text-zinc-700">
                            {{ row.importedPostId ? `#${row.importedPostId}` : '-' }}
                          </td>
                        </tr>
                        <tr v-if="!importJobReportRows.length">
                          <td
                            colspan="4"
                            class="px-4 py-8 text-center text-zinc-400"
                          >
                            Report rows will appear as posts are processed.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            </div>

            <div class="flex items-center justify-between gap-3 border-t bg-zinc-50 px-6 py-4">
              <button
                type="button"
                class="px-3 py-1.5 text-sm text-zinc-600 hover:text-zinc-900 disabled:opacity-50"
                :disabled="isStartImportLoading || isPreviewLoading"
                @click="handleImportBack"
              >
                {{ importWizardStep === 1 ? 'Close' : 'Back' }}
              </button>

              <div class="flex items-center gap-2">
                <button
                  v-if="importWizardStep === 1"
                  type="button"
                  class="inline-flex items-center rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  :disabled="isPreviewLoading || !canRunPreview"
                  @click="handleImportPreview"
                >
                  {{ isPreviewLoading ? 'Previewing...' : 'Run Dry-Run Preview' }}
                </button>
                <button
                  v-if="importWizardStep === 2"
                  type="button"
                  class="inline-flex items-center rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  :disabled="isStartImportLoading || !canStartImport"
                  @click="handleStartImport"
                >
                  {{ isStartImportLoading ? 'Starting...' : 'Start Import' }}
                </button>
                <button
                  v-if="importWizardStep === 3"
                  type="button"
                  class="inline-flex items-center rounded-md bg-zinc-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-zinc-800"
                  @click="closeImportWizard"
                >
                  Done
                </button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <TransitionRoot
    as="template"
    :show="backPostComposerOpen"
  >
    <Dialog
      as="div"
      class="relative z-[85]"
      @close="handleBackPostComposerRequestClose"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-150"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-zinc-950/30 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 z-[85] flex items-center justify-center px-4 sm:px-6">
        <TransitionChild
          as="template"
          enter="ease-out duration-150"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-100"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <DialogPanel class="w-full max-w-2xl overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
            <div class="relative flex items-center justify-between border-b border-zinc-200 px-6 py-4">
              <div>
                <DialogTitle class="text-xl font-semibold text-zinc-900">
                  Create Back Post
                </DialogTitle>
                <p class="mt-1 text-xs text-zinc-500">
                  Create a single historical post with custom posted-as identity and publish date.
                </p>
              </div>
              <button
                type="button"
                class="rounded-full p-1.5 text-zinc-500 bg-zinc-200 hover:bg-zinc-300 hover:text-zinc-700"
                @click="closeBackPostComposer"
              >
                <span class="sr-only">Close</span>
                <XMarkIcon class="w-6 h-6" />
              </button>
            </div>

            <div class="px-6 py-5 space-y-5 max-h-[72vh] overflow-y-auto app-scroll">
              <section class="rounded-xl border border-zinc-200 p-4 space-y-3">
                <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div class="md:col-span-2">
                    <label class="block text-xs font-medium text-zinc-700">
                      Find member
                    </label>
                    <input
                      v-model="backPostMemberSearch"
                      type="text"
                      class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Search by member name or email"
                      @focus="handleBackPostMemberSearchFocus"
                    >
                    <div class="mt-2 rounded-lg border border-zinc-200 bg-zinc-50">
                      <div
                        class="max-h-52 overflow-y-auto app-scroll"
                        @scroll="handleBackPostMemberResultsScroll"
                      >
                        <button
                          v-for="memberOption in visibleBackPostMemberOptions"
                          :key="`quick-member-${memberOption.userId}`"
                          type="button"
                          class="flex w-full items-center gap-2 border-b border-zinc-100 px-3 py-2 text-left hover:bg-zinc-100 last:border-b-0"
                          @click="selectBackPostAttributedMember(memberOption.userId)"
                        >
                          <img
                            v-if="memberOption.avatarUrl"
                            :src="memberOption.avatarUrl"
                            alt="Member avatar"
                            class="h-7 w-7 rounded-full object-cover"
                          >
                          <div
                            v-else
                            class="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-600"
                          >
                            {{ memberOption.name?.slice(0, 1) || 'M' }}
                          </div>
                          <div class="min-w-0 flex-1">
                            <p class="truncate text-xs font-semibold text-zinc-800">
                              {{ memberOption.name }}
                            </p>
                            <p class="truncate text-[11px] text-zinc-500">
                              {{ memberOption.email || `Member #${memberOption.userId}` }}
                            </p>
                          </div>
                        </button>
                        <p
                          v-if="!isLoadingBackPostMembers && !visibleBackPostMemberOptions.length"
                          class="px-3 py-3 text-xs text-zinc-500"
                        >
                          No members match your search.
                        </p>
                      </div>
                      <div
                        v-if="!isLoadingBackPostMembers && hasMoreFilteredBackPostMembers"
                        class="border-t border-zinc-200 px-3 py-2"
                      >
                        <button
                          type="button"
                          class="text-xs font-semibold text-[#2D8CFF] hover:underline"
                          @click="loadMoreFilteredBackPostMembers"
                        >
                          Load more members
                        </button>
                      </div>
                      <p
                        v-if="isLoadingBackPostMembers"
                        class="border-t border-zinc-200 px-3 py-2 text-xs text-zinc-400"
                      >
                        Loading members...
                      </p>
                    </div>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-xs font-medium text-zinc-700">
                      Attribute this post to
                    </label>
                    <PlatformaSelect
                      v-model="backPostAttributedMemberUserId"
                      :options="backPostMemberSelectOptions"
                      placeholder="Creator (default)"
                      allow-empty
                      @menu-scroll-bottom="loadMoreFilteredBackPostMembers"
                    />
                    <p class="mt-1 text-xs text-zinc-500">
                      Selecting a member attributes this post to that member in feed/search results.
                    </p>
                  </div>
                </div>
              </section>

              <section class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label class="block text-xs font-medium text-zinc-700">Posted as name</label>
                  <input
                    v-model="backPostPostedAsName"
                    type="text"
                    class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Display name shown on this post"
                  >
                </div>
                <div>
                  <label class="block text-xs font-medium text-zinc-700">Posted as avatar</label>
                  <div class="mt-1 flex items-center gap-3">
                    <div class="h-10 w-10 overflow-hidden rounded-full border border-zinc-200 bg-zinc-100">
                      <img
                        v-if="backPostPostedAsAvatarUrl"
                        :src="backPostPostedAsAvatarUrl"
                        alt="Back post avatar"
                        class="h-full w-full object-cover"
                      >
                    </div>
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-md border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
                      :disabled="backPostAvatarUploading"
                      @click="triggerBackPostAvatarPicker"
                    >
                      <PhotoIcon class="h-4 w-4" />
                      {{ backPostAvatarUploading ? 'Uploading...' : 'Upload Avatar' }}
                    </button>
                    <input
                      ref="backPostAvatarInputRef"
                      type="file"
                      accept="image/*"
                      class="hidden"
                      @change="handleBackPostAvatarSelected"
                    >
                  </div>
                </div>
              </section>

              <section>
                <label class="block text-xs font-medium text-zinc-700">Post date/time</label>
                <input
                  v-model="backPostDateTimeLocal"
                  type="datetime-local"
                  :max="backPostDateTimeMax"
                  class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
              </section>

              <section>
                <label class="block text-xs font-medium text-zinc-700">Post content</label>
                <textarea
                  v-model="backPostContent"
                  rows="6"
                  class="mt-1 block w-full rounded-md border border-zinc-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Write the post content..."
                />
              </section>

              <section class="rounded-xl border border-zinc-200 p-4 space-y-3">
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <p class="text-sm font-medium text-zinc-800">
                      Media attachments
                    </p>
                    <p class="text-xs text-zinc-500">
                      Optional image/video attachments for this back post.
                    </p>
                  </div>
                  <button
                    type="button"
                    class="inline-flex items-center gap-2 rounded-md border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
                    :disabled="backPostMediaUploading"
                    @click="triggerBackPostMediaPicker"
                  >
                    <PhotoIcon class="h-4 w-4" />
                    {{ backPostMediaUploading ? 'Uploading...' : 'Upload Media' }}
                  </button>
                  <input
                    ref="backPostMediaInputRef"
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    class="hidden"
                    @change="handleBackPostMediaSelected"
                  >
                </div>

                <div
                  v-if="backPostMediaItems.length"
                  class="grid grid-cols-2 gap-2 md:grid-cols-3"
                >
                  <div
                    v-for="item in backPostMediaItems"
                    :key="item.fileId"
                    class="relative overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100"
                  >
                    <img
                      v-if="item.kind === 'image'"
                      :src="item.previewUrl"
                      alt="Attachment preview"
                      class="h-24 w-full object-cover"
                    >
                    <div
                      v-else
                      class="h-24 w-full bg-zinc-900 text-white text-xs flex items-center justify-center"
                    >
                      Video
                    </div>
                    <button
                      type="button"
                      class="absolute right-1 top-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white hover:bg-black/80"
                      @click="removeBackPostMediaItem(item.fileId)"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </section>

              <p
                v-if="backPostError"
                class="text-sm text-red-600"
              >
                {{ backPostError }}
              </p>
              <p
                v-if="backPostSuccess"
                class="text-sm text-emerald-600"
              >
                {{ backPostSuccess }}
              </p>
            </div>

            <div class="flex items-center justify-end gap-2 border-t bg-zinc-50 px-6 py-4">
              <button
                type="button"
                class="px-3 py-1.5 text-sm text-zinc-600 hover:text-zinc-900"
                @click="closeBackPostComposer"
              >
                Cancel
              </button>
              <button
                type="button"
                class="inline-flex items-center rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                :disabled="backPostSaving || !canCreateBackPost"
                @click="handleCreateBackPost"
              >
                {{ backPostSaving ? 'Creating...' : 'Create Back Post' }}
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import {
  ArrowDownTrayIcon,
  PhotoIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { useMediaUploadStore } from '@/stores/mediaUploadStore';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  featureInstanceId: { type: [Number, String, null], default: null },
});

const emit = defineEmits(['close', 'saved']);

const localName = ref('');
const localAvatarUrl = ref('');
const localHideMemberCount = ref(false);
const localRequirePostApproval = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);

// Defaults from creator profile (used when no settings saved yet)
const defaultProfileName = ref('');
const defaultProfileAvatarUrl = ref('');

// ðŸ”¹ media upload store (reuses your existing upload pipeline)
const mediaUploadStore = useMediaUploadStore();

// ðŸ”¹ file input ref for avatar picker
const avatarFileInputRef = ref(null);
let importJobPollTimer = null;

const importWizardOpen = ref(false);
const importWizardStep = ref(1);
const importSourceType = ref('platforma_newsfeed_instance');
const importSourceFeatureInstanceId = ref(null);
const importCsvPayload = ref('');
const importCsvFileName = ref('');
const importTimeframe = ref('last_30_days');
const importCustomStartDate = ref('');
const importCustomEndDate = ref('');
const importIncludeMedia = ref(true);
const importDedupeMode = ref('skip_exact');
const isLoadingImportSources = ref(false);
const importSourceOptions = ref([]);
const isPreviewLoading = ref(false);
const importPreview = ref(null);
const importError = ref('');
const isStartImportLoading = ref(false);
const importJob = ref(null);
const importJobReportRows = ref([]);
const isRollbackLoading = ref(false);
const backPostComposerOpen = ref(false);
const backPostSaving = ref(false);
const backPostMediaUploading = ref(false);
const backPostAvatarUploading = ref(false);
const backPostError = ref('');
const backPostSuccess = ref('');
const backPostContent = ref('');
const backPostPostedAsName = ref('');
const backPostPostedAsAvatarUrl = ref('');
const backPostDateTimeLocal = ref('');
const backPostMediaItems = ref([]);
const backPostAttributedMemberUserId = ref('');
const backPostMemberSearch = ref('');
const backPostMemberOptions = ref([]);
const isLoadingBackPostMembers = ref(false);
const suppressBackPostCloseOnSettingsClose = ref(false);
const backPostMediaInputRef = ref(null);
const backPostAvatarInputRef = ref(null);
const backPostMemberVisibleCount = ref(100);
let backPostComposerOpenTimer = null;

const importSourceTypeSelectOptions = [
  { value: 'platforma_newsfeed_instance', label: 'Platforma Newsfeed Instance' },
  { value: 'legacy_newsfeed_csv', label: 'Legacy CSV Export' },
];

const importTimeframeSelectOptions = [
  { value: 'all_time', label: 'All time' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

const importDedupeModeSelectOptions = [
  { value: 'skip_exact', label: 'Skip exact duplicates' },
  { value: 'skip_source', label: 'Skip already imported source posts' },
  { value: 'import_all', label: 'Import all posts' },
];

const importWizardStepLabel = computed(() => {
  if (importWizardStep.value === 1) return 'Configure import';
  if (importWizardStep.value === 2) return 'Dry-run preview';
  return 'Run and report';
});

const importSourceInstanceSelectOptions = computed(() =>
  (Array.isArray(importSourceOptions.value) ? importSourceOptions.value : []).map((source) => ({
    value: Number(source.id),
    label: String(source.name || `Instance ${source.id}`),
  }))
);

const importProgressPercent = computed(() => {
  const raw = Number(importJob.value?.progress?.percent || 0);
  if (!Number.isFinite(raw)) return 0;
  return Math.min(100, Math.max(0, Math.round(raw)));
});

const canRunPreview = computed(() => {
  const targetInstanceId = Number(props.featureInstanceId);
  if (!Number.isFinite(targetInstanceId) || targetInstanceId <= 0) {
    return false;
  }

  if (
    importTimeframe.value === 'custom' &&
    (!importCustomStartDate.value || !importCustomEndDate.value)
  ) {
    return false;
  }

  if (importSourceType.value === 'legacy_newsfeed_csv') {
    return importCsvPayload.value.trim().length > 0;
  }

  const sourceInstanceId = Number(importSourceFeatureInstanceId.value);
  return Number.isFinite(sourceInstanceId) &&
    sourceInstanceId > 0 &&
    sourceInstanceId !== targetInstanceId;
});

const canStartImport = computed(() =>
  !!importPreview.value &&
  (Number(importPreview.value?.sourceCount || 0) > 0)
);

const canRollbackImport = computed(() =>
  importJob.value?.status === 'completed' &&
  Number(importJob.value?.progress?.importedRows || 0) > 0
);

const backPostDateTimeMax = computed(() => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
});

const canCreateBackPost = computed(() => {
  const hasDate = String(backPostDateTimeLocal.value || '').trim().length > 0;
  const hasText = String(backPostContent.value || '').trim().length > 0;
  const hasMedia = Array.isArray(backPostMediaItems.value) && backPostMediaItems.value.length > 0;
  return hasDate && (hasText || hasMedia);
});

const filteredBackPostMemberOptions = computed(() => {
  const list = Array.isArray(backPostMemberOptions.value)
    ? backPostMemberOptions.value
    : [];
  const search = String(backPostMemberSearch.value || '').trim().toLowerCase();
  if (!search) return list;

  return list.filter((memberOption) => {
    const name = String(memberOption?.name || '').toLowerCase();
    const email = String(memberOption?.email || '').toLowerCase();
    return name.includes(search) || email.includes(search);
  });
});

const visibleBackPostMemberOptions = computed(() =>
  filteredBackPostMemberOptions.value.slice(0, backPostMemberVisibleCount.value)
);

const hasMoreFilteredBackPostMembers = computed(
  () => visibleBackPostMemberOptions.value.length < filteredBackPostMemberOptions.value.length
);

const backPostMemberSelectOptions = computed(() => {
  const selectedMemberId = Number(backPostAttributedMemberUserId.value || 0);
  const visibleMap = new Map(
    visibleBackPostMemberOptions.value.map((memberOption) => [Number(memberOption.userId), memberOption])
  );

  const selectedMember = (backPostMemberOptions.value || []).find(
    (memberOption) => Number(memberOption.userId) === selectedMemberId
  );
  if (selectedMember && !visibleMap.has(selectedMemberId)) {
    visibleMap.set(selectedMemberId, selectedMember);
  }

  const options = Array.from(visibleMap.values())
    .map((memberOption) => ({
      value: String(memberOption.userId),
      label: `${memberOption.name}${memberOption.email ? ` (${memberOption.email})` : ''}`,
      iconSrc: memberOption.avatarUrl || '',
      iconAlt: memberOption.name || 'Member avatar',
      iconClass: 'h-5 w-5 rounded-full object-cover',
    }))
    .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));

  return options;
});

function emitClose() {
  if (isSaving.value || importWizardOpen.value || backPostComposerOpen.value) return;
  emit('close');
}

function clearImportJobPollTimer() {
  if (importJobPollTimer) {
    clearInterval(importJobPollTimer);
    importJobPollTimer = null;
  }
}

function clearBackPostComposerOpenTimer() {
  if (backPostComposerOpenTimer) {
    clearTimeout(backPostComposerOpenTimer);
    backPostComposerOpenTimer = null;
  }
}

function resetImportWizardState() {
  importWizardStep.value = 1;
  importError.value = '';
  importPreview.value = null;
  importJob.value = null;
  importJobReportRows.value = [];
  importCsvFileName.value = '';
  isPreviewLoading.value = false;
  isStartImportLoading.value = false;
  isRollbackLoading.value = false;
  clearImportJobPollTimer();
}

function openImportWizard() {
  resetImportWizardState();
  importWizardOpen.value = true;
  void fetchImportSourceInstances();
}

function closeImportWizard() {
  importWizardOpen.value = false;
  clearImportJobPollTimer();
}

function handleImportWizardRequestClose() {
  // Keep the wizard open during native select interactions.
  // Users can still close with explicit controls (X / Back / Cancel).
}

function resetBackPostComposerState() {
  backPostError.value = '';
  backPostSuccess.value = '';
  backPostSaving.value = false;
  backPostMediaUploading.value = false;
  backPostAvatarUploading.value = false;
  backPostContent.value = '';
  backPostPostedAsName.value = '';
  backPostPostedAsAvatarUrl.value = '';
  backPostMediaItems.value = [];
  backPostAttributedMemberUserId.value = '';
  backPostMemberSearch.value = '';
  backPostMemberVisibleCount.value = 100;
  backPostDateTimeLocal.value = backPostDateTimeMax.value;
}

function openBackPostComposer() {
  resetBackPostComposerState();
  backPostPostedAsName.value = String(localName.value || defaultProfileName.value || '').trim();
  backPostPostedAsAvatarUrl.value = String(localAvatarUrl.value || defaultProfileAvatarUrl.value || '').trim();
  if (!Array.isArray(backPostMemberOptions.value) || !backPostMemberOptions.value.length) {
    void fetchBackPostMembers();
  }
  suppressBackPostCloseOnSettingsClose.value = true;
  emit('close');
  clearBackPostComposerOpenTimer();
  backPostComposerOpenTimer = setTimeout(async () => {
    await nextTick();
    backPostComposerOpen.value = true;
    suppressBackPostCloseOnSettingsClose.value = false;
    backPostComposerOpenTimer = null;
  }, 160);
}

function closeBackPostComposer() {
  clearBackPostComposerOpenTimer();
  backPostComposerOpen.value = false;
  suppressBackPostCloseOnSettingsClose.value = false;
}

function handleBackPostComposerRequestClose() {
  // Keep open during native picker/dropdown interactions.
}

function handleBackPostMemberSearchFocus() {
  if (!Array.isArray(backPostMemberOptions.value) || !backPostMemberOptions.value.length) {
    void fetchBackPostMembers();
  }
}

function loadMoreFilteredBackPostMembers() {
  if (!hasMoreFilteredBackPostMembers.value) return;
  backPostMemberVisibleCount.value += 100;
}

function handleBackPostMemberResultsScroll(event) {
  if (!hasMoreFilteredBackPostMembers.value) return;
  const target = event?.target;
  if (!target) return;
  const remaining = Number(target.scrollHeight) - Number(target.scrollTop) - Number(target.clientHeight);
  if (remaining <= 24) {
    loadMoreFilteredBackPostMembers();
  }
}

function selectBackPostAttributedMember(memberUserId) {
  const normalized = Number(memberUserId);
  if (!Number.isFinite(normalized) || normalized <= 0) {
    backPostAttributedMemberUserId.value = '';
    return;
  }
  backPostAttributedMemberUserId.value = String(normalized);
}

function formatImportDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
}

async function handleImportCsvFileSelected(event) {
  const file = event?.target?.files?.[0];
  if (!file) return;

  try {
    const text = await file.text();
    importCsvPayload.value = String(text || '');
    importCsvFileName.value = file.name || 'legacy-export.csv';
    importError.value = '';
  } catch (error) {
    console.error('[NewsfeedSettingsModal] handleImportCsvFileSelected error:', error);
    importError.value = 'Failed to read CSV file.';
  } finally {
    if (event?.target) {
      event.target.value = '';
    }
  }
}

function triggerBackPostMediaPicker() {
  if (backPostMediaInputRef.value) {
    backPostMediaInputRef.value.click();
  }
}

function triggerBackPostAvatarPicker() {
  if (backPostAvatarInputRef.value) {
    backPostAvatarInputRef.value.click();
  }
}

async function handleBackPostAvatarSelected(event) {
  const file = event?.target?.files?.[0];
  if (!file) return;

  backPostError.value = '';
  backPostSuccess.value = '';
  backPostAvatarUploading.value = true;
  try {
    const uploadResult = await mediaUploadStore.uploadSingleFile(
      file,
      props.featureInstanceId || null,
      { origin: 'creator-newsfeed-back-post-avatar' }
    );

    const fileId = getBackPostUploadFileId(uploadResult);
    if (!fileId) {
      throw new Error(`Upload returned no file ID for "${file.name}"`);
    }

    backPostPostedAsAvatarUrl.value = `${apiProxyUrl}/api/image/${encodeURIComponent(fileId)}`;
  } catch (error) {
    console.error('[NewsfeedSettingsModal] handleBackPostAvatarSelected error:', error);
    backPostError.value = String(error?.message || 'Failed to upload avatar');
  } finally {
    backPostAvatarUploading.value = false;
    if (event?.target) {
      event.target.value = '';
    }
  }
}

function getBackPostUploadFileId(result) {
  return String(
    result?.fileId ||
    result?.file_id ||
    (Array.isArray(result?.files) && (result.files[0]?.fileId || result.files[0]?.file_id)) ||
    ''
  ).trim();
}

function getBackPostUploadPreviewUrl(result) {
  return String(
    result?.thumbnailUrl ||
    result?.thumbnail_url ||
    result?.fileUrl ||
    result?.file_url ||
    ''
  ).trim();
}

async function handleBackPostMediaSelected(event) {
  const files = Array.from(event?.target?.files || []);
  if (!files.length) return;

  backPostError.value = '';
  backPostSuccess.value = '';
  backPostMediaUploading.value = true;
  try {
    for (const file of files) {
      const uploadResult = await mediaUploadStore.uploadSingleFile(
        file,
        props.featureInstanceId || null,
        { origin: 'creator-newsfeed-back-post' }
      );

      const fileId = getBackPostUploadFileId(uploadResult);
      if (!fileId) {
        throw new Error(`Upload returned no file ID for "${file.name}"`);
      }

      const mimeType = String(file.type || uploadResult?.mimeType || uploadResult?.mime_type || '').toLowerCase();
      const kind = mimeType.startsWith('video/') ? 'video' : 'image';
      const previewUrl = getBackPostUploadPreviewUrl(uploadResult) || `${apiProxyUrl}/api/image/${encodeURIComponent(fileId)}`;

      if (!backPostMediaItems.value.some((item) => item.fileId === fileId)) {
        backPostMediaItems.value.push({
          fileId,
          kind,
          previewUrl,
        });
      }
    }
  } catch (error) {
    console.error('[NewsfeedSettingsModal] handleBackPostMediaSelected error:', error);
    backPostError.value = String(error?.message || 'Failed to upload media');
  } finally {
    backPostMediaUploading.value = false;
    if (event?.target) {
      event.target.value = '';
    }
  }
}

function removeBackPostMediaItem(fileId) {
  backPostMediaItems.value = backPostMediaItems.value.filter(
    (item) => item.fileId !== fileId
  );
}

async function fetchImportSourceInstances() {
  isLoadingImportSources.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/feature-instances`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Failed to load feature instances (${response.status})`);
    }

    const payload = await response.json();
    const rawList =
      payload.instances ||
      payload.featureInstances ||
      payload.items ||
      (Array.isArray(payload) ? payload : []);

    const options = Array.isArray(rawList)
      ? rawList
          .filter((instance) => {
            const featureId = Number(
              instance.feature_id ??
              instance.featureId ??
              instance.feature?.feature_id ??
              instance.feature?.id
            );
            const featureName = String(
              instance.feature_name ||
              instance.featureName ||
              instance.feature?.name ||
              ''
            ).toLowerCase();
            const instanceSlug = String(
              instance.slug ||
              instance.feature_slug ||
              instance.featureSlug ||
              ''
            ).toLowerCase();
            return (Number.isFinite(featureId) && featureId === 6) || featureName.includes('newsfeed') || instanceSlug.includes('newsfeed');
          })
          .map((instance) => ({
            id: Number(instance.instance_id || instance.instanceId || instance.id),
            name: String(instance.custom_name || instance.customName || instance.name || `Newsfeed #${instance.instance_id || instance.instanceId || instance.id || ''}`).trim(),
          }))
          .filter((instance) => Number.isFinite(instance.id) && instance.id > 0 && instance.id !== Number(props.featureInstanceId))
      : [];

    importSourceOptions.value = options;
    if (!importSourceFeatureInstanceId.value && options.length) {
      importSourceFeatureInstanceId.value = options[0].id;
    }
  } catch (error) {
    console.error('[NewsfeedSettingsModal] fetchImportSourceInstances error:', error);
    importSourceOptions.value = [];
    importError.value = 'Failed to load source newsfeed instances.';
  } finally {
    isLoadingImportSources.value = false;
  }
}

function buildImportPayload() {
  const targetInstanceId = Number(props.featureInstanceId);
  const sourceInstanceId =
    importSourceType.value === 'legacy_newsfeed_csv'
      ? (
        Number.isFinite(Number(importSourceFeatureInstanceId.value)) &&
        Number(importSourceFeatureInstanceId.value) > 0
          ? Number(importSourceFeatureInstanceId.value)
          : targetInstanceId
      )
      : Number(importSourceFeatureInstanceId.value);

  return {
    sourceType: importSourceType.value,
    sourceFeatureInstanceId: sourceInstanceId,
    targetFeatureInstanceId: targetInstanceId,
    timeframe: importTimeframe.value,
    customStartDate: importTimeframe.value === 'custom' ? importCustomStartDate.value || null : null,
    customEndDate: importTimeframe.value === 'custom' ? importCustomEndDate.value || null : null,
    includeMedia: !!importIncludeMedia.value,
    dedupeMode: importDedupeMode.value,
    csvPayload:
      importSourceType.value === 'legacy_newsfeed_csv'
        ? importCsvPayload.value
        : '',
  };
}

async function handleImportPreview() {
  if (!canRunPreview.value) {
    importError.value = 'Please choose a valid source and timeframe first.';
    return;
  }
  importError.value = '';
  isPreviewLoading.value = true;
  importPreview.value = null;

  try {
    const response = await fetch(`${apiProxyUrl}/api/creator-newsfeed/imports/preview`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildImportPayload()),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Preview failed (${response.status})`);
    }

    importPreview.value = data.preview || null;
    importWizardStep.value = 2;
  } catch (error) {
    console.error('[NewsfeedSettingsModal] handleImportPreview error:', error);
    importError.value = String(error?.message || 'Failed to preview import');
  } finally {
    isPreviewLoading.value = false;
  }
}

async function pollImportJob(jobPublicId) {
  if (!jobPublicId) return;

  try {
    const response = await fetch(`${apiProxyUrl}/api/creator-newsfeed/imports/${encodeURIComponent(jobPublicId)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Failed to refresh job (${response.status})`);
    }

    importJob.value = data.job || null;
    importJobReportRows.value = Array.isArray(data?.report?.rows) ? data.report.rows : [];

    const status = String(importJob.value?.status || '').toLowerCase();
    if (['completed', 'failed', 'rolled_back'].includes(status)) {
      clearImportJobPollTimer();
    }
  } catch (error) {
    console.error('[NewsfeedSettingsModal] pollImportJob error:', error);
    importError.value = String(error?.message || 'Failed to refresh import job');
    clearImportJobPollTimer();
  }
}

async function handleStartImport() {
  if (!canStartImport.value) {
    importError.value = 'Preview the import first.';
    return;
  }
  importError.value = '';
  isStartImportLoading.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/creator-newsfeed/imports/start`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildImportPayload()),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Failed to start import (${response.status})`);
    }

    importWizardStep.value = 3;
    importJob.value = data.job || null;
    importJobReportRows.value = [];

    clearImportJobPollTimer();
    if (importJob.value?.jobPublicId) {
      await pollImportJob(importJob.value.jobPublicId);
      importJobPollTimer = setInterval(() => {
        void pollImportJob(importJob.value?.jobPublicId);
      }, 2000);
    }
  } catch (error) {
    console.error('[NewsfeedSettingsModal] handleStartImport error:', error);
    importError.value = String(error?.message || 'Failed to start import');
  } finally {
    isStartImportLoading.value = false;
  }
}

async function handleCreateBackPost() {
  if (!canCreateBackPost.value || backPostSaving.value) return;

  backPostError.value = '';
  backPostSuccess.value = '';
  backPostSaving.value = true;
  try {
    const parsedDate = new Date(backPostDateTimeLocal.value);
    if (Number.isNaN(parsedDate.getTime())) {
      throw new Error('Please choose a valid post date/time.');
    }

    const payload = {
      featureInstanceId: Number(props.featureInstanceId),
      content: String(backPostContent.value || '').trim(),
      postedAsName: String(backPostPostedAsName.value || '').trim() || null,
      postedAsAvatarUrl: String(backPostPostedAsAvatarUrl.value || '').trim() || null,
      attributedMemberUserId: Number(backPostAttributedMemberUserId.value) || null,
      mediaFileIds: backPostMediaItems.value.map((item) => item.fileId),
      coverMediaFileId: backPostMediaItems.value.length ? backPostMediaItems.value[0].fileId : null,
      backdatedAt: parsedDate.toISOString(),
    };

    const response = await fetch(`${apiProxyUrl}/api/creator-newsfeed/back-posts`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Failed to create back post (${response.status})`);
    }

    backPostSuccess.value = 'Back post created successfully.';
    emit('saved', data.post || null);
  } catch (error) {
    console.error('[NewsfeedSettingsModal] handleCreateBackPost error:', error);
    backPostError.value = String(error?.message || 'Failed to create back post');
  } finally {
    backPostSaving.value = false;
  }
}

async function handleRollbackImport() {
  if (!importJob.value?.jobPublicId) return;
  isRollbackLoading.value = true;
  importError.value = '';
  try {
    const response = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/imports/${encodeURIComponent(importJob.value.jobPublicId)}/rollback`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Rollback failed (${response.status})`);
    }
    await pollImportJob(importJob.value.jobPublicId);
  } catch (error) {
    console.error('[NewsfeedSettingsModal] handleRollbackImport error:', error);
    importError.value = String(error?.message || 'Failed to rollback import');
  } finally {
    isRollbackLoading.value = false;
  }
}

function handleImportBack() {
  if (importWizardStep.value === 1) {
    closeImportWizard();
    return;
  }
  if (importWizardStep.value === 3 && importJob.value?.jobPublicId) {
    const status = String(importJob.value?.status || '').toLowerCase();
    if (['queued', 'running'].includes(status)) {
      importError.value = 'Import is still running. Wait for completion before leaving this step.';
      return;
    }
  }
  importWizardStep.value = Math.max(1, importWizardStep.value - 1);
}

function useProfileAvatar() {
  if (defaultProfileAvatarUrl.value) {
    localAvatarUrl.value = defaultProfileAvatarUrl.value;
  }
  if (!localName.value && defaultProfileName.value) {
    localName.value = defaultProfileName.value;
  }
}

function triggerAvatarFilePicker() {
  if (avatarFileInputRef.value) {
    avatarFileInputRef.value.click();
  }
}


async function handleAvatarFileSelected(event) {
  const file = event?.target?.files?.[0];
  if (!file) return;

  try {
    const result = await mediaUploadStore.uploadSingleFile(
      file,
      props.featureInstanceId || null,
      {
        origin: 'creator-newsfeed-posting-avatar',
      }
    );

    console.log('[NewsfeedSettingsModal] avatar upload result:', result);

    // Try to find a fileId in the typical shapes your store uses
    const fileId =
      result?.fileId ||
      result?.file_id ||
      (Array.isArray(result?.files) && result.files[0]?.fileId) ||
      (Array.isArray(result?.files) && result.files[0]?.file_id) ||
      null;

    if (fileId) {
      // âœ… Use the same streaming route as other images
      const streamingUrl = `${apiProxyUrl}/api/image/${fileId}`;
      localAvatarUrl.value = streamingUrl;
    } else if (result?.fileUrl) {
      // Fallback: at least don't break if fileId is missing
      console.warn(
        '[NewsfeedSettingsModal] No fileId in upload result, falling back to fileUrl',
        result
      );
      localAvatarUrl.value = result.fileUrl;
    } else {
      console.warn(
        '[NewsfeedSettingsModal] Avatar upload returned no usable URL or fileId',
        result
      );
      alert('Avatar uploaded but no URL was returned. Please check console.');
    }
  } catch (err) {
    console.error('[NewsfeedSettingsModal] handleAvatarFileSelected error:', err);
    alert('Failed to upload avatar image. Please try again.');
  } finally {
    // reset input so selecting the same file again still fires change
    if (avatarFileInputRef.value) {
      avatarFileInputRef.value.value = '';
    }
  }
}



async function fetchCreatorDefaults() {
  try {
    // 1) Creator name
    const nameRes = await fetch(`${apiProxyUrl}/api/creator-details`, {
      method: 'GET',
      credentials: 'include',
    });
    if (nameRes.ok) {
      const data = await nameRes.json();
      const first = data.firstName || '';
      const last = data.lastName || '';
      defaultProfileName.value = last ? `${first} ${last}` : first || '';
    }

    // 2) Creator main profile pic
    const imgRes = await fetch(
      `${apiProxyUrl}/api/uploads/get-image?imageType=${encodeURIComponent(
        'creator_profile_pic_main'
      )}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    if (imgRes.ok) {
      const imgData = await imgRes.json();
      defaultProfileAvatarUrl.value = imgData.fileUrl || '';
    }
  } catch (err) {
    console.error('[NewsfeedSettingsModal] fetchCreatorDefaults error:', err);
  }
}

function normalizeBackPostMemberOption(memberRow) {
  const userId = Number(
    memberRow?.userId ??
    memberRow?.user_id ??
    memberRow?.id ??
    memberRow?.memberUserId ??
    0
  );
  if (!Number.isFinite(userId) || userId <= 0) return null;

  const firstName = String(
    memberRow?.firstName ??
    memberRow?.first_name ??
    ''
  ).trim();
  const lastName = String(
    memberRow?.lastName ??
    memberRow?.last_name ??
    ''
  ).trim();
  const fullNameFromParts = `${firstName} ${lastName}`.trim();
  const displayName = String(
    memberRow?.name ??
    memberRow?.alias ??
    memberRow?.handle ??
    fullNameFromParts ??
    ''
  ).trim() || `Member ${userId}`;
  const email = String(memberRow?.email || '').trim();
  const rawAvatarUrl = String(
    memberRow?.avatarUrl ??
    memberRow?.profile_pic ??
    memberRow?.profilePic ??
    memberRow?.image ??
    ''
  ).trim();
  const avatarUrl = rawAvatarUrl === 'src/assets/media/images/default-avatar.png'
    ? ''
    : rawAvatarUrl;

  return {
    userId,
    name: displayName,
    email,
    avatarUrl,
  };
}

async function fetchBackPostMembers() {
  if (isLoadingBackPostMembers.value) return;
  isLoadingBackPostMembers.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/members`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || `Failed to fetch members (${response.status})`);
    }

    const rows = Array.isArray(data?.members) ? data.members : [];
    backPostMemberOptions.value = rows
      .map((row) => normalizeBackPostMemberOption(row))
      .filter(Boolean)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  } catch (error) {
    console.error('[NewsfeedSettingsModal] fetchBackPostMembers error:', error);
    backPostMemberOptions.value = [];
  } finally {
    isLoadingBackPostMembers.value = false;
  }
}

async function fetchInstanceSettings() {
  const instanceId = props.featureInstanceId;
  if (!instanceId) return;

  isLoading.value = true;
  try {
    const searchParams = new URLSearchParams({
      featureInstanceId: String(instanceId),
    }).toString();

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/instance-settings?${searchParams}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!res.ok) {
      console.error(
        '[NewsfeedSettingsModal] GET instance-settings not OK:',
        res.status,
        res.statusText
      );
      return;
    }

    const data = await res.json();
    const s = data.settings || {};

    localName.value = s.creatorPostingAsName || '';
    localAvatarUrl.value = s.creatorPostingAsAvatarUrl || '';
    localHideMemberCount.value = !!s.hideMemberCount;
    localRequirePostApproval.value = !!s.requirePostApproval;

    // Seed from defaults when nothing saved yet
    if (!localName.value && defaultProfileName.value) {
      localName.value = defaultProfileName.value;
    }
    if (!localAvatarUrl.value && defaultProfileAvatarUrl.value) {
      localAvatarUrl.value = defaultProfileAvatarUrl.value;
    }
  } catch (err) {
    console.error('[NewsfeedSettingsModal] fetchInstanceSettings error:', err);
  } finally {
    isLoading.value = false;
  }
}

async function handleSave() {
  const instanceId = props.featureInstanceId;
  if (!instanceId) return;

  isSaving.value = true;
  try {
    const payload = {
      featureInstanceId: Number(instanceId),
      creatorPostingAsName: localName.value || null,
      creatorPostingAsAvatarUrl: localAvatarUrl.value || null,
      hideMemberCount: !!localHideMemberCount.value,
      requirePostApproval: !!localRequirePostApproval.value,
    };

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/instance-settings`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      console.error(
        '[NewsfeedSettingsModal] POST instance-settings not OK:',
        res.status,
        res.statusText
      );
      alert('Failed to save settings. Please try again.');
      return;
    }

    const data = await res.json();
    emit('saved', data.settings || payload);
    emitClose();
  } catch (err) {
    console.error('[NewsfeedSettingsModal] handleSave error:', err);
    alert('Failed to save settings. Please try again.');
  } finally {
    isSaving.value = false;
  }
}

// When modal opens, load defaults + settings
watch(
  () => props.isOpen,
  async (open) => {
    if (open && props.featureInstanceId) {
      await fetchCreatorDefaults();
      await fetchInstanceSettings();
    } else if (!open) {
      closeImportWizard();
      if (!suppressBackPostCloseOnSettingsClose.value) {
        closeBackPostComposer();
      }
    }
  }
);

// If instance changes while open, reload settings
watch(
  () => props.featureInstanceId,
  async (newId, oldId) => {
    if (props.isOpen && newId && newId !== oldId) {
      await fetchInstanceSettings();
    }
  }
);

watch(
  () => importSourceType.value,
  (nextType) => {
    importError.value = '';
    importPreview.value = null;
    if (nextType === 'legacy_newsfeed_csv') {
      return;
    }
    if (
      (!Number.isFinite(Number(importSourceFeatureInstanceId.value)) ||
      Number(importSourceFeatureInstanceId.value) <= 0) &&
      importSourceOptions.value.length
    ) {
      importSourceFeatureInstanceId.value = importSourceOptions.value[0].id;
    }
  }
);

watch(
  () => importWizardOpen.value,
  (open) => {
    if (!open) {
      clearImportJobPollTimer();
    }
  }
);

watch(
  () => backPostAttributedMemberUserId.value,
  (selectedValue) => {
    const memberUserId = Number(selectedValue);
    if (!Number.isFinite(memberUserId) || memberUserId <= 0) {
      backPostPostedAsName.value = String(localName.value || defaultProfileName.value || '').trim();
      backPostPostedAsAvatarUrl.value = String(localAvatarUrl.value || defaultProfileAvatarUrl.value || '').trim();
      return;
    }

    const selectedMember = (backPostMemberOptions.value || []).find(
      (memberOption) => Number(memberOption?.userId) === memberUserId
    );
    if (!selectedMember) return;

    backPostPostedAsName.value = String(
      selectedMember.name || selectedMember.email || `Member ${memberUserId}`
    ).trim();
  backPostPostedAsAvatarUrl.value = String(selectedMember.avatarUrl || '').trim();
  }
);

watch(
  () => backPostMemberSearch.value,
  () => {
    backPostMemberVisibleCount.value = 100;
  }
);

onBeforeUnmount(() => {
  clearImportJobPollTimer();
  clearBackPostComposerOpenTimer();
});
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(113, 113, 122, 0.42) transparent;
}

.app-scroll::-webkit-scrollbar {
  width: 7px;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(113, 113, 122, 0.35);
}

.app-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(82, 82, 91, 0.5);
}
</style>

